import { DISPOSABLE_EMAIL_DOMAINS } from "./disposable-email-domains";



const GMAIL_DOMAINS = new Set([
  "gmail.com",
  "googlemail.com",
]);

const PLUS_ADDRESSING_DOMAINS = new Set([
  "outlook.com",
  "hotmail.com",
  "live.com",
  "msn.com",
  "hotmail.co.uk", "hotmail.fr", "hotmail.de", "hotmail.it", "hotmail.es",
  "hotmail.ca", "hotmail.com.au", "hotmail.com.br", "hotmail.co.jp",
  "outlook.co.uk", "outlook.fr", "outlook.de", "outlook.it", "outlook.es",
  "outlook.com.au", "outlook.com.br", "outlook.co.jp",
  "live.co.uk", "live.fr", "live.de", "live.it", "live.es",
  "live.com.au", "live.com.mx", "live.co.jp",
  "protonmail.com",
  "proton.me",
  "pm.me",
  "yahoo.com",
  "ymail.com",
  "yahoo.co.uk", "yahoo.fr", "yahoo.de", "yahoo.it", "yahoo.es",
  "yahoo.ca", "yahoo.com.au", "yahoo.com.br", "yahoo.co.jp",
  "yahoo.co.in", "yahoo.com.mx", "yahoo.ie",
  "icloud.com",
  "me.com",
  "mac.com",
  "fastmail.com",
  "fastmail.fm",
  "hey.com",
  "zoho.com",
  "aol.com",
  "gmx.com", "gmx.de", "gmx.net",
  "mail.com",
]);

export function sanitizeEmailForStorage(email: string): string {
  if (!email) return "";
  return email.toLowerCase().trim();
}

export function emailDomain(email: string): string | null {
  const trimmed = email.toLowerCase().trim();
  const atIndex = trimmed.lastIndexOf("@");
  return atIndex === -1 ? null : trimmed.slice(atIndex + 1);
}

export function normalizeEmailForLookup(email: string): string {
  if (!email) return "";

  const trimmed = email.toLowerCase().trim();
  const atIndex = trimmed.lastIndexOf("@");

  if (atIndex === -1) return trimmed;

  let local = trimmed.slice(0, atIndex);
  let domain = trimmed.slice(atIndex + 1);

  if (GMAIL_DOMAINS.has(domain)) {
    const plusIndex = local.indexOf("+");
    if (plusIndex !== -1) {
      local = local.slice(0, plusIndex);
    }
    local = local.replace(/\./g, "");
    domain = "gmail.com";
  }
  else if (PLUS_ADDRESSING_DOMAINS.has(domain)) {
    const plusIndex = local.indexOf("+");
    if (plusIndex !== -1) {
      local = local.slice(0, plusIndex);
    }
  }

  return `${local}@${domain}`;
}

const PROTON_DOMAINS = new Set([
  "proton.me",
  "protonmail.com",
  "pm.me",
  "protonmail.ch",
]);

export type CanonicalEmail = string & { readonly __canonicalEmail: true };

export function canonicalEmail(email: string): CanonicalEmail | null {
  const normalized = normalizeEmailForLookup(email);
  const atIndex = normalized.lastIndexOf("@");
  if (atIndex === -1) return null;

  let local = normalized.slice(0, atIndex);
  let domain = normalized.slice(atIndex + 1);
  if (PROTON_DOMAINS.has(domain)) {
    local = local.split("+")[0].replace(/[._-]/g, "");
    domain = "proton.me";
  }

  return local && domain ? (`${local}@${domain}` as CanonicalEmail) : null;
}

const ADDITIONAL_DISPOSABLE_EMAIL_DOMAINS = new Set([
  "tempmail.com",
  "throwaway.email",
  "tempmailaddress.com",
  "burnermail.io",
  "33mail.com",
  "spamgourmet.com",
  "permmail.com",
]);

export function isDisposableEmail(email: string): boolean {
  const domain = emailDomain(email);
  if (!domain) return false;
  return DISPOSABLE_EMAIL_DOMAINS.has(domain) || ADDITIONAL_DISPOSABLE_EMAIL_DOMAINS.has(domain);
}

const TRIAL_DENIED_EMAIL_DOMAINS: ReadonlySet<string> = new Set([
  "hypeaco.com",
  "oofsong.com",
  "sunkenway.com",
  "chahalempire.com",
  "mhill5.xyz",
  "wapium.com",
  "gemail.pk",
  "atomicmail.io",
  "atomicmail.com",
]);

export function isTrialDeniedEmail(email: string): boolean {
  const domain = emailDomain(email);
  if (!domain) return false;
  const labels = domain.split(".");
  return (
    isDisposableEmail(email) ||
    labels.some((_, index) => TRIAL_DENIED_EMAIL_DOMAINS.has(labels.slice(index).join(".")))
  );
}

export function validateEmailForSignup(email: string): {
  valid: boolean;
  error?: string;
} {
  const trimmed = email.toLowerCase().trim();

  if (!trimmed || !trimmed.includes("@")) {
    return {
      valid: false,
      error: "Please enter a valid email address.",
    };
  }

  if (isDisposableEmail(trimmed)) {
    return {
      valid: false,
      error: "Please use a permanent email address. Temporary email services are not allowed.",
    };
  }

  return { valid: true };
}
